import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import {
    Tag,
    Percent,
    DollarSign,
    Calendar,
    Save,
    Trash2,
    RefreshCw,
    Search,
    ChevronLeft,
    Eye,
    EyeOff
} from 'lucide-react';

const ProductDiscount = () => {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [discountForm, setDiscountForm] = useState({
        type: 'percentage',
        value: '',
        startDate: '',
        endDate: ''
    });

    useEffect(() => {
        fetchProducts();
    }, []);

    const fetchProducts = async () => {
        try {
            const { data } = await axios.get('/api/v1/admin/products', {
                withCredentials: true
            });
            setProducts(data.products);
        } catch (error) {
            toast.error('Error fetching products');
        } finally {
            setLoading(false);
        }
    };

    const handleApplyDiscount = async (productId) => {
        try {
            const { data } = await axios.put(
                `/api/v1/admin/product/${productId}/discount`,
                discountForm,
                { withCredentials: true }
            );
            if (data.success) {
                toast.success('Discount applied successfully');
                fetchProducts();
                setSelectedProduct(null);
                setDiscountForm({
                    type: 'percentage',
                    value: '',
                    startDate: '',
                    endDate: ''
                });
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Error applying discount');
        }
    };

    const handleRemoveDiscount = async (productId) => {
        try {
            const { data } = await axios.delete(
                `/api/v1/admin/product/${productId}/discount`,
                { withCredentials: true }
            );
            if (data.success) {
                toast.success('Discount removed successfully');
                fetchProducts();
            }
        } catch (error) {
            toast.error('Error removing discount');
        }
    };

    // New function to handle toggling product visibility
    const handleToggleVisibility = async (productId, currentVisibility) => {
        try {
            const { data } = await axios.patch(
                `/api/v1/admin/product/${productId}/toggle-visibility`,
                {},
                { withCredentials: true }
            );
            if (data.success) {
                toast.success(`Product is now ${data.isVisible ? 'visible' : 'hidden'}`);
                // Update the product in the local state
                setProducts(products.map(p => 
                    p._id === productId ? {...p, isVisible: data.isVisible} : p
                ));
            }
        } catch (error) {
            toast.error('Error toggling product visibility');
        }
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center min-h-screen">
                <RefreshCw className="h-7 w-7 text-[#dc2626] animate-spin" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-3 sm:p-5">
            <div className="max-w-7xl mx-auto">
                {/* Header with title and back button */}
                <div className="flex justify-between items-center mb-4">
                    <h1 className="text-lg sm:text-xl font-bold text-gray-900 font-['Crimson_Text']">
                        Product Discounts
                    </h1>
                    <button
                        onClick={() => navigate('/admin/dashboard')}
                        className="flex items-center gap-1 sm:gap-2 text-gray-600 hover:text-[#dc2626] transition-colors py-1.5 px-2.5 rounded-lg hover:bg-gray-100"
                    >
                        <ChevronLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        <span className="text-xs sm:text-sm hidden xs:inline">Back to Dashboard</span>
                    </button>
                </div>
                {/* Search bar */}
                <div className="mb-4">
                    <div className="relative w-full sm:w-64">
                        <input
                            type="text"
                            placeholder="Search products..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-8 sm:pl-9 pr-3 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                        />
                        <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    </div>
                </div>
                <div className="grid grid-cols-1 gap-3 sm:gap-4">
                    {filteredProducts.map(product => (
                        <div
                            key={product._id}
                            className="bg-white rounded-lg shadow-sm p-3 sm:p-4 hover:shadow-md transition-shadow"
                        >
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                                {/* Product info */}
                                <div className="flex items-center space-x-3 mb-3 sm:mb-0">
                                    <img
                                        src={product.images[0].image}
                                        alt={product.name}
                                        className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-lg"
                                    />
                                    <div>
                                        <h3 className="text-sm sm:text-base font-medium text-gray-900">{product.name}</h3>
                                        <div className="flex items-center text-xs text-gray-500 space-x-2">
                                            <span>{product.category}</span>
                                            {/* Visibility indicator */}
                                            <span className={`px-1.5 py-0.5 rounded-full ${product.isVisible ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                                {product.isVisible ? 'Visible' : 'Hidden'}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                {/* Current Discount Display and Visibility Toggle */}
                                {product.discount?.active ? (
                                    <div className="flex justify-between sm:justify-end items-center sm:space-x-3">
                                        <div className="text-xs sm:text-sm">
                                            <p className="font-medium text-gray-900">
                                                {product.discount.type === 'percentage' ? (
                                                    <span className="flex items-center">
                                                        <Percent className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                                                        {product.discount.value}% OFF
                                                    </span>
                                                ) : (
                                                    <span className="flex items-center">
                                                        <DollarSign className="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-1" />
                                                        ₹{product.discount.value} OFF
                                                    </span>
                                                )}
                                            </p>
                                            <p className="text-gray-500 text-xs">
                                                Until {new Date(product.discount.endDate).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex gap-1.5">
                                            {/* Visibility toggle button */}
                                            <button
                                                onClick={() => handleToggleVisibility(product._id, product.isVisible)}
                                                className={`p-1.5 sm:p-2 ${product.isVisible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'} rounded-lg transition-colors`}
                                                title={product.isVisible ? 'Hide Product' : 'Show Product'}
                                            >
                                                {product.isVisible ? <Eye className="w-4 h-4 sm:w-4.5 sm:h-4.5" /> : <EyeOff className="w-4 h-4 sm:w-4.5 sm:h-4.5" />}
                                            </button>
                                            <button
                                                onClick={() => handleRemoveDiscount(product._id)}
                                                className="p-1.5 sm:p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                            >
                                                <Trash2 className="w-4 h-4 sm:w-4.5 sm:h-4.5" />
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex items-center gap-2">
                                        {/* Visibility toggle button */}
                                        <button
                                            onClick={() => handleToggleVisibility(product._id, product.isVisible)}
                                            className={`p-1.5 sm:p-2 ${product.isVisible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-50'} rounded-lg transition-colors`}
                                            title={product.isVisible ? 'Hide Product' : 'Show Product'}
                                        >
                                            {product.isVisible ? <Eye className="w-4 h-4 sm:w-4.5 sm:h-4.5" /> : <EyeOff className="w-4 h-4 sm:w-4.5 sm:h-4.5" />}
                                        </button>
                                        <button
                                            onClick={() => setSelectedProduct(product)}
                                            className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors self-start sm:self-center"
                                        >
                                            Add Discount
                                        </button>
                                    </div>
                                )}
                            </div>
                            {/* Discount Form */}
                            {selectedProduct?._id === product._id && (
                                <div className="mt-3 sm:mt-4 border-t pt-3 sm:pt-4">
                                    <h4 className="text-sm sm:text-base font-medium text-gray-900 mb-3">
                                        Add Discount
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Discount Type
                                            </label>
                                            <select
                                                value={discountForm.type}
                                                onChange={(e) => setDiscountForm(prev => ({
                                                    ...prev,
                                                    type: e.target.value
                                                }))}
                                                className="block w-full pl-2.5 pr-8 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                            >
                                                <option value="percentage">Percentage</option>
                                                <option value="amount">Fixed Amount</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                {discountForm.type === 'percentage' ? 'Percentage' : 'Amount'}
                                            </label>
                                            <input
                                                type="number"
                                                value={discountForm.value}
                                                onChange={(e) => setDiscountForm(prev => ({
                                                    ...prev,
                                                    value: e.target.value
                                                }))}
                                                className="block w-full pl-2.5 pr-8 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                                placeholder={discountForm.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                Start Date
                                            </label>
                                            <input
                                                type="date"
                                                value={discountForm.startDate}
                                                onChange={(e) => setDiscountForm(prev => ({
                                                    ...prev,
                                                    startDate: e.target.value
                                                }))}
                                                className="block w-full pl-2.5 pr-8 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-medium text-gray-700 mb-1">
                                                End Date
                                            </label>
                                            <input
                                                type="date"
                                                value={discountForm.endDate}
                                                onChange={(e) => setDiscountForm(prev => ({
                                                    ...prev,
                                                    endDate: e.target.value
                                                }))}
                                                className="block w-full pl-2.5 pr-8 py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg focus:ring-[#dc2626] focus:border-[#dc2626]"
                                            />
                                        </div>
                                    </div>
                                    <div className="mt-3 sm:mt-4 flex justify-end space-x-2 sm:space-x-3">
                                        <button
                                            onClick={() => setSelectedProduct(null)}
                                            className="px-3 py-1.5 sm:px-3.5 sm:py-1.5 text-xs sm:text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={() => handleApplyDiscount(product._id)}
                                            className="px-3 py-1.5 sm:px-3.5 sm:py-1.5 text-xs sm:text-sm bg-[#dc2626] text-white rounded-lg hover:bg-[#dc2626]/90 transition-colors"
                                        >
                                            Apply Discount
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {filteredProducts.length === 0 && (
                        <div className="text-center py-8 bg-white rounded-lg shadow-sm">
                            <Tag className="w-10 h-10 sm:w-12 sm:h-12 text-gray-400 mx-auto mb-3" />
                            <h3 className="text-sm sm:text-base font-medium text-gray-900 mb-1">No Products Found</h3>
                            <p className="text-xs sm:text-sm text-gray-500">
                                {searchTerm ? 'Try a different search term.' : 'There are no products in the system.'}
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductDiscount;